
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUserRoles } from '@/hooks/useUserRoles';
import { TeamCostDisplay } from './TeamCostDisplay';

interface TeamTrienniaDisplayProps {
  teamId: string;
}

// Helper function to validate UUID format
const isValidUUID = (uuid: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);
};

export const TeamTrienniaDisplay = ({
  teamId
}: TeamTrienniaDisplayProps) => {
  const { t } = useLanguage();
  const { isAdmin } = useUserRoles();
  
  const { data: averageTrietia, isLoading } = useQuery({
    queryKey: ['team-average-trietia', teamId],
    queryFn: async () => {
      try {
        console.log("Calculating average trietia for team:", teamId);
        
        if (!isValidUUID(teamId)) {
          console.error(`Invalid team ID format: ${teamId}`);
          return null;
        }
        
        // First get team members
        const { data: members, error: membersError } = await supabase
          .from('team_members')
          .select('profile_id')
          .eq('team_id', teamId);
          
        if (membersError) {
          console.error("Error fetching team members:", membersError);
          throw membersError;
        }
        
        if (!members || members.length === 0) {
          console.log("No members found for team:", teamId);
          return null;
        }

        console.log("Team members found:", members.length);
        
        // Make sure we have valid UUIDs before querying profiles
        const validProfileIds = members
          .filter(m => m.profile_id && isValidUUID(m.profile_id))
          .map(m => m.profile_id);

        if (validProfileIds.length === 0) {
          console.log("No valid profile IDs found");
          return null;
        }

        console.log("Valid profile IDs:", validProfileIds.length);
        
        // Get trietia for each member
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('trietia')
          .in('id', validProfileIds);
          
        if (profilesError) {
          console.error("Error fetching profiles:", profilesError);
          throw profilesError;
        }
        
        if (!profiles || profiles.length === 0) {
          console.log("No profiles found with trietia data");
          return null;
        }

        console.log("Profiles with trietia data:", profiles.length);
        
        // Calculate average trietia
        let totalTrietia = 0;
        let validMembersCount = 0;
        
        profiles.forEach(profile => {
          if (profile.trietia !== null && profile.trietia !== undefined) {
            const trietia = parseInt(String(profile.trietia || 0), 10);
            if (!isNaN(trietia)) {
              totalTrietia += trietia;
              validMembersCount++;
              console.log(`Adding trietia value: ${trietia} (total now: ${totalTrietia}, count: ${validMembersCount})`);
            }
          }
        });

        if (validMembersCount === 0) {
          console.log("No valid trietia values found");
          return null;
        }
        
        const average = totalTrietia / validMembersCount;
        console.log(`Final average trietia: ${average} (total: ${totalTrietia}, count: ${validMembersCount})`);
        return average;
      } catch (error) {
        console.error('Error fetching team trietia:', error);
        return null;
      }
    },
    enabled: isAdmin
  });
  
  if (!isAdmin || isLoading || averageTrietia === null) return null;
  
  return (
    <TeamCostDisplay
      cost={averageTrietia}
      tooltipText="Average Trietia"
      displayLabel="Μέση Τριετία"
      borderClass="border-amber-500"
      textClass="text-amber-600"
      isCurrency={false}
    />
  );
};
